import React, { useEffect, useMemo } from 'react';
import { getIn, useFormikContext } from 'formik';
import { Button, Form } from 'react-bootstrap';
import { FaMinus, FaPlus } from 'react-icons/fa';
import { FormCol12, FieldInput } from 'motion-components';
import { FileRepoField } from './filerepo-field';

type FileRepoFieldListProps = {
  label: string;
  fieldName: string;
  arrayFieldPath: string;
  disabled?: boolean;
  /** Μέγιστος αριθμός αρχείων. Default: 5. */
  maxFiles?: number;
  /** Αν true, εμφανίζεται πεδίο σχολίου κάτω από κάθε αρχείο. Default: false. */
  showNotes?: boolean;
  notesLabel?: string;
  /** Path στο Formik errors για required error (π.χ. xrbData.fileDetailsRequired.batteryPhotoFileId). Αν υπάρχει error εδώ, εμφανίζεται στο πρώτο row. */
  requiredErrorPath?: string;
};

type FileDetailRow = {
  fieldName?: string;
  fileId?: number | null;
  sortOrder?: number | null;
  notes?: string | null;
  /** Όνομα αρχείου αμέσως μετά upload, πριν φορτωθεί το fileRep από API */
  pendingFileName?: string | null;
  fileRep?: any;
};

const ensureSortedRows = (rows: FileDetailRow[], fieldName: string): FileDetailRow[] => {
  const withSort = rows.map((row, idx) => ({
    ...row,
    fieldName,
    sortOrder: idx + 1,
  }));
  return withSort;
};

const FileRepoFieldListRow = ({
  label,
  fieldName,
  arrayFieldPath,
  absoluteIndex,
  disabled,
  showLabel,
  isVirtual,
  onAddRow,
  onRemoveRow,
  allowRemove,
  canAddRow,
  showAddButton,
  showNotes,
  notesLabel,
  photoIndex,
  photoCount,
  requiredError,
}: {
  label: string;
  fieldName: string;
  arrayFieldPath: string;
  absoluteIndex: number;
  disabled: boolean;
  showLabel: boolean;
  isVirtual: boolean;
  onAddRow: () => void;
  onRemoveRow: (absoluteIndex: number) => void;
  allowRemove: boolean;
  canAddRow: boolean;
  showAddButton: boolean;
  showNotes: boolean;
  notesLabel: string;
  photoIndex: number;
  photoCount: number;
  requiredError?: string;
}) => {
  const { values, setFieldValue } = useFormikContext<any>();
  const row: FileDetailRow | undefined = getIn(values, `${arrayFieldPath}[${absoluteIndex}]`);
  const fileId = row?.fileId;
  const fileName = (row?.fileRep?.fileName || row?.pendingFileName || '').trim();

  // If user uploads into a "virtual" row (not yet in initialValues),
  // ensure we also persist metadata (fieldName/sortOrder) into the array.
  useEffect(() => {
    if (fileId == null) return;
    const currentFieldName = row?.fieldName;
    const currentSortOrder = row?.sortOrder;

    if (currentFieldName !== fieldName) {
      setFieldValue(`${arrayFieldPath}[${absoluteIndex}].fieldName`, fieldName, false);
    }
    if (currentSortOrder == null) {
      setFieldValue(`${arrayFieldPath}[${absoluteIndex}].sortOrder`, 1, false);
    }
  }, [
    absoluteIndex,
    arrayFieldPath,
    fieldName,
    fileId,
    row?.fieldName,
    row?.sortOrder,
    setFieldValue,
  ]);

  /** Label σχολίου με αναφορά στο συγκεκριμένο αρχείο */
  const notesFieldLabel = (() => {
    if (fileName) {
      return `${notesLabel} για: ${fileName}`;
    }
    if (photoCount > 1) {
      return `${notesLabel} για φωτογραφία ${photoIndex}`;
    }
    return `${notesLabel} για την παραπάνω φωτογραφία`;
  })();

  const rowContent = (
    <>
      <div className="d-flex align-items-end">
        <div className="flex-grow-1 me-1">
          <FileRepoField
            as={FormCol12}
            label={showLabel ? label : ''}
            fileIdFieldName={`${arrayFieldPath}[${absoluteIndex}].fileId`}
            fileNameFieldName={`${arrayFieldPath}[${absoluteIndex}].pendingFileName`}
            fileRec={row?.fileRep}
            canEdit={!disabled}
          />
        </div>
        {(showAddButton || (allowRemove && !isVirtual)) && (
          <div
            className="d-flex align-items-center flex-shrink-0 pb-2"
            style={{ marginBottom: '3px' }}
          >
            {showAddButton && (
              <Button
                variant="success"
                onClick={onAddRow}
                disabled={disabled || !canAddRow}
                title="Προσθήκη νέου αρχείου"
                aria-label="Προσθήκη νέου αρχείου"
                style={{
                  width: '22px',
                  height: '22px',
                  padding: 0,
                  borderRadius: '999px',
                  marginRight: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <FaPlus style={{ fontSize: '12px' }} />
              </Button>
            )}
            {allowRemove && !isVirtual && (
              <Button
                variant="danger"
                onClick={() => onRemoveRow(absoluteIndex)}
                disabled={disabled}
                title="Διαγραφή αρχείου"
                aria-label="Διαγραφή αρχείου"
                style={{
                  width: '22px',
                  height: '22px',
                  padding: 0,
                  borderRadius: '999px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <FaMinus style={{ fontSize: '12px' }} />
              </Button>
            )}
          </div>
        )}
      </div>
      {requiredError && (
        <Form.Control.Feedback
          type="invalid"
          className="d-block"
          style={{ marginTop: '-6px', marginBottom: '6px' }}
        >
          {requiredError}
        </Form.Control.Feedback>
      )}
      {showNotes && fileId != null && (
        <div
          style={{
            marginTop: '4px',
            paddingTop: '6px',
            borderTop: '1px dashed #ced4da',
          }}
        >
          <FieldInput
            label={notesFieldLabel}
            name={`${arrayFieldPath}[${absoluteIndex}].notes`}
            disabled={disabled}
            placeholder=""
          />
        </div>
      )}
    </>
  );

  return (
    <div className="mb-2">
      {showNotes && fileId != null ? (
        <div
          style={{
            border: '1px solid #dee2e6',
            borderLeft: '3px solid #6c9bd2',
            borderRadius: '4px',
            backgroundColor: '#f8f9fb',
            padding: '8px 10px 4px',
          }}
        >
          {rowContent}
        </div>
      ) : (
        rowContent
      )}
    </div>
  );
};

export const FileRepoFieldList = ({
  label,
  fieldName,
  arrayFieldPath,
  disabled = false,
  maxFiles = 5,
  showNotes = false,
  notesLabel = 'Σχόλιο / παρατήρηση',
  requiredErrorPath,
}: FileRepoFieldListProps) => {
  const { values, setFieldValue, errors } = useFormikContext<any>();
  const allRows: FileDetailRow[] = getIn(values, arrayFieldPath) || [];

  const requiredError =
    requiredErrorPath != null
      ? (getIn(errors, requiredErrorPath) as string | undefined)
      : undefined;

  const matchingRows = useMemo(
    () =>
      allRows
        .map((row, index) => ({ row, index }))
        .filter((item) => item.row?.fieldName === fieldName)
        .sort((a, b) => (a.row?.sortOrder ?? 9999) - (b.row?.sortOrder ?? 9999)),
    [allRows, fieldName],
  );

  // Νέα γραμμή μόνο όταν όλα τα slots έχουν αρχείο και δεν έχει συμπληρωθεί το όριο
  const canAddRow =
    matchingRows.length < maxFiles && matchingRows.every((item) => item.row?.fileId != null);
  const showAddButton = matchingRows.length < maxFiles;

  // Ensure each field list has its own dedicated row in Formik state.
  // This avoids multiple lists binding to the same virtual index/path.
  useEffect(() => {
    if (matchingRows.length > 0) return;
    setFieldValue(arrayFieldPath, [...allRows, { fieldName, fileId: null, sortOrder: 1, notes: null, pendingFileName: null }], false);
  }, [allRows, arrayFieldPath, fieldName, matchingRows.length, setFieldValue]);

  const updateRows = (nextAllRows: FileDetailRow[]) => {
    const keptRows = nextAllRows.filter((row) => row?.fieldName !== fieldName);
    const ownRows = nextAllRows.filter((row) => row?.fieldName === fieldName);
    const normalized = [...keptRows, ...ensureSortedRows(ownRows, fieldName)];
    setFieldValue(arrayFieldPath, normalized);
  };

  const addRow = () => {
    const ownRows = allRows.filter((row) => row?.fieldName === fieldName);
    if (ownRows.length >= maxFiles) return;
    updateRows([...allRows, { fieldName, fileId: null, sortOrder: ownRows.length + 1, notes: null, pendingFileName: null }]);
  };

  const removeRow = (absoluteIndex: number) => {
    const nextAllRows = allRows.filter((_, idx) => idx !== absoluteIndex);
    updateRows(nextAllRows);
  };

  return (
    <div>
      {matchingRows.map(({ index }, idx) => (
        <FileRepoFieldListRow
          key={`${fieldName}-${index}`}
          label={label}
          fieldName={fieldName}
          arrayFieldPath={arrayFieldPath}
          absoluteIndex={index}
          disabled={disabled}
          showLabel={idx === 0}
          isVirtual={false}
          onAddRow={addRow}
          onRemoveRow={removeRow}
          allowRemove={matchingRows.length > 1}
          canAddRow={canAddRow}
          showAddButton={showAddButton}
          showNotes={showNotes}
          notesLabel={notesLabel}
          photoIndex={idx + 1}
          photoCount={matchingRows.length}
          requiredError={idx === 0 ? requiredError : undefined}
        />
      ))}
    </div>
  );
};
